// Balanța elevului, o singură sursă pe filă: bara de sus și widget-ul „Balanță” citesc de aici.
// O cerere la pornire, apoi soldul vine singur prin socket („balance:update”) când personalul
// îl schimbă (sau, mai târziu, după o plată). Cât timp balanța e oprită pe server: doar /balance/config.
import { useContext, useEffect, useSyncExternalStore } from "react";

import { UserContext } from "../UserContext";
import { getBalanceConfig, getMyBalance } from "../api/balanceService";
import { acquireSocket, releaseSocket } from "../socket/socket";

const ENTRIES = 5;
const VISIBLE_RELOAD_MS = 30 * 1000; // la revenirea pe filă, cel mult o dată la 30 s

const INITIAL = { loaded: false, enabled: false, balanceBani: 0, entries: [], updatedAt: 0 };

let state = INITIAL;
const listeners = new Set();
let owner = null; // id-ul elevului pentru care e starea
let mounted = 0;
let socket = null;
let reloadTimer = 0;
let lastReloadAt = 0;
let generation = 0; // crește la oprire / schimbarea contului: răspunsurile vechi se ignoră

function emit(patch) {
   state = { ...state, ...patch };
   listeners.forEach((l) => l());
}

function readAccessToken() {
   if (typeof document === "undefined") return "";
   const match = document.cookie.match(/(?:^|;\s*)access_token=([^;]+)/);
   return match ? decodeURIComponent(match[1]) : "";
}

async function reload() {
   const gen = generation;
   lastReloadAt = Date.now();
   try {
      const mine = await getMyBalance({ limit: ENTRIES });
      if (gen !== generation) return;
      emit({
         balanceBani: Number(mine?.balanceBani) || 0,
         entries: Array.isArray(mine?.entries) ? mine.entries : [],
         updatedAt: Date.now(),
      });
   } catch {
      // rămâne ultimul sold cunoscut
   }
}

function onSocketUpdate(payload) {
   if (owner && payload?.userId && Number(payload.userId) !== Number(owner)) return;
   if (typeof payload?.balanceBani === "number") emit({ balanceBani: payload.balanceBani, updatedAt: Date.now() });
   // istoria (ultimele operații) se reîncarcă imediat după
   clearTimeout(reloadTimer);
   reloadTimer = setTimeout(reload, 300);
}

function onSocketConnect() {
   try {
      socket?.emit("register");
   } catch {}
}

function onVisible() {
   if (document.visibilityState === "visible" && state.enabled && Date.now() - lastReloadAt > VISIBLE_RELOAD_MS) reload();
}

function connect() {
   if (socket) return;
   socket = acquireSocket(readAccessToken());
   if (!socket) return;
   socket.on("connect", onSocketConnect);
   socket.on("balance:update", onSocketUpdate);
   if (socket.connected) onSocketConnect();
   document.addEventListener("visibilitychange", onVisible);
}

function disconnect() {
   clearTimeout(reloadTimer);
   document.removeEventListener("visibilitychange", onVisible);
   if (!socket) return;
   socket.off("connect", onSocketConnect);
   socket.off("balance:update", onSocketUpdate);
   releaseSocket(socket);
   socket = null;
}

async function start() {
   const gen = generation;
   try {
      const config = await getBalanceConfig();
      if (gen !== generation) return;
      if (!config?.enabled) return emit({ loaded: true, enabled: false });
      await reload();
      if (gen !== generation) return;
      emit({ loaded: true, enabled: true });
      connect();
   } catch {
      if (gen === generation) emit({ loaded: true });
   }
}

function stop() {
   generation += 1;
   disconnect();
   state = INITIAL;
   owner = null;
}

function subscribe(listener) {
   listeners.add(listener);
   return () => listeners.delete(listener);
}

const getSnapshot = () => state;

/** { loaded, enabled, balanceBani, entries } — pentru elevul logat; fără elev nu face nimic. */
export default function useStudentBalance() {
   const { user } = useContext(UserContext) || {};
   const userId = user?.role === "USER" ? user?.id : null;

   useEffect(() => {
      if (!userId) return undefined;
      if (owner !== userId) {
         if (owner !== null) stop();
         owner = userId;
         start();
      }
      mounted += 1;
      return () => {
         mounted -= 1;
         // ultimul care o folosea a plecat (de ex. pe ecran lat, ieșirea de pe pagina principală)
         if (mounted === 0) stop();
      };
   }, [userId]);

   return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// pentru teste
export const __balanceStore = { onSocketUpdate, getState: () => state, reset: stop, setOwner: (id) => (owner = id) };
